import { useEffect, useState } from "react";
import { BarChart3, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type MonthCount = { month: string; count: number };

type Statistics = {
  auctionsLast30: number;
  auctionsLast30Change: number;
  avgBidValue: number | null;
  usersLast30: number;
  usersLast30Change: number;
  activeOrganizations: number;
  closedAuctionsLast30: number;
  conversionRate: number;
  auctionsByMonth: MonthCount[];
  usersByMonth: MonthCount[];
};

const formatNOK = (n: number) =>
  new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(n);

function ChangeLabel({ pct }: { pct: number }) {
  if (pct === 0) return <span className="text-xs text-muted-foreground">Ingen endring</span>;
  const positive = pct > 0;
  return (
    <span className={`text-xs font-medium ${positive ? "text-green-600" : "text-red-500"}`}>
      {positive ? "+" : ""}{pct}% vs forrige 30 dager
    </span>
  );
}

function BarChart({ data }: { data: MonthCount[] }) {
  if (!data.length) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground/50">
        Ingen data tilgjengelig
      </div>
    );
  }

  const H = 120;
  const LABEL_H = 20;
  const n = data.length;
  const PAD = 3;
  const totalW = 100;
  const barW = (totalW - PAD * (n + 1)) / n;

  const max = Math.max(...data.map((d) => d.count), 1);

  return (
    <svg viewBox={`0 0 ${totalW} ${H + LABEL_H}`} className="w-full h-40" preserveAspectRatio="none">
      {data.map((d, i) => {
        const barH = Math.max((d.count / max) * H, 2);
        const x = PAD + i * (barW + PAD);
        const y = H - barH;
        const label = new Date(d.month + "-02").toLocaleDateString("nb-NO", { month: "short" });
        return (
          <g key={d.month}>
            <rect x={x} y={y} width={barW} height={barH} rx="1.5" fill="hsl(var(--accent))" fillOpacity="0.85" />
            <text
              x={x + barW / 2}
              y={H + LABEL_H - 3}
              textAnchor="middle"
              fontSize="5.5"
              fill="currentColor"
              opacity="0.45"
            >
              {label}
            </text>
            {d.count > 0 && (
              <text
                x={x + barW / 2}
                y={y - 2}
                textAnchor="middle"
                fontSize="5"
                fill="currentColor"
                opacity="0.5"
              >
                {d.count}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function StatistikkPage() {
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetch_ = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/admin/statistics`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const data = (await res.json()) as Statistics;
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetch_();
    return () => { cancelled = true; };
  }, []);

  const val = (n: number | null | undefined, fmt?: (n: number) => string) => {
    if (loading) return "...";
    if (n == null) return "—";
    return fmt ? fmt(n) : n.toLocaleString("nb-NO");
  };

  const metrics = [
    {
      label: "Nye auksjoner (siste 30 dager)",
      value: val(stats?.auctionsLast30),
      extra: stats && !loading ? <ChangeLabel pct={stats.auctionsLast30Change} /> : null,
    },
    {
      label: "Gjennomsnittlig budverdi",
      value: val(stats?.avgBidValue, formatNOK),
      extra: <span className="text-xs text-muted-foreground">Auksjoner med bud</span>,
    },
    {
      label: "Nye brukere (siste 30 dager)",
      value: val(stats?.usersLast30),
      extra: stats && !loading ? <ChangeLabel pct={stats.usersLast30Change} /> : null,
    },
    {
      label: "Aktive organisasjoner",
      value: val(stats?.activeOrganizations),
      extra: <span className="text-xs text-muted-foreground">Med live auksjoner nå</span>,
    },
    {
      label: "Lukkede auksjoner (siste 30 dager)",
      value: val(stats?.closedAuctionsLast30),
      extra: <span className="text-xs text-muted-foreground">Avsluttede auksjoner</span>,
    },
    {
      label: "Konverteringsrate",
      value: loading ? "..." : stats ? `${stats.conversionRate}%` : "—",
      extra: <span className="text-xs text-muted-foreground">Lukkede auksjoner med bud</span>,
    },
  ];

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      <div>
        <h1
          className="text-2xl font-black tracking-tight text-foreground"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Statistikk
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Plattformstatistikk og trender</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        {metrics.map(({ label, value, extra }) => (
          <Card key={label} className="p-4 sm:p-5">
            <p className="text-xs font-medium text-muted-foreground leading-tight">{label}</p>
            <p className="mt-2 text-2xl font-black tracking-tight text-foreground">{value}</p>
            <div className="mt-1">{extra}</div>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Auksjonsoversikt</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground/50">Laster...</div>
          ) : (
            <BarChart data={stats?.auctionsByMonth ?? []} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Brukervekst</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground/50">Laster...</div>
          ) : (
            <BarChart data={stats?.usersByMonth ?? []} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
